// Shared bits for admin tables that pin their right-most column.
//
// These tables decide column visibility with *viewport* breakpoints (lg:, xl:)
// while living inside a width-capped card, so on mid-size screens every column
// renders into a container too narrow to hold them. The overflow always lands
// on the last column — which is the actions column — leaving buttons clipped
// and, without a scroll wrapper, completely unreachable. Pinning that column
// keeps it usable at any width and however many columns the data adds.
import { useCallback, useRef, useState } from 'react';

// Applied to the pinned <th>/<td>. The cell needs its own opaque background,
// since content scrolls underneath it.
export const STICKY_CELL = 'sticky right-0';
export const STICKY_HEAD_BG = 'z-10 bg-slate-50';
export const STICKY_CELL_BG = 'bg-white group-hover:bg-slate-50';
export const STICKY_SHADOW = 'shadow-[-8px_0_10px_-8px_rgba(15,23,42,0.12)]';

// Only shadow the pinned column while the table can actually scroll under it —
// a permanent shadow reads as a stray column border on wide screens.
export function useScrollShadow() {
  const [shadowed, setShadowed] = useState(false);
  const disconnect = useRef(null);

  // A callback ref, not useEffect: these tables render conditionally (loading /
  // empty states), so the node attaches long after mount.
  const ref = useCallback((el) => {
    disconnect.current?.();
    disconnect.current = null;
    if (!el) return;
    const update = () => setShadowed(el.scrollWidth > el.clientWidth + 1);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    // Also watch the table itself: it grows when rows load, while the
    // wrapper's own size never changes.
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    disconnect.current = () => ro.disconnect();
  }, []);

  return [ref, shadowed];
}
